// VoiceDock — global voice assistant dock.
//
// A popover that grows from the mic button instead of a fullscreen modal, with
// explicit voice-cycle states (record → recognise → think → answer) and an
// inline mini-dialogue. Collapses to a bar above the bottom-nav and survives
// navigation between sections. Replaces the old fullscreen VoiceModal.

import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { T, Icon, Btn } from '../../tokens';
import type { IconName } from '../../tokens';
import { AISummary, AIText, AIClarify } from '../chat';
import type { Message } from '../chat';
import { LiveWaveform } from './LiveWaveform';
import type { VoiceDockController } from './useVoiceDock';
import './voicedock.css';

export interface VoiceDockProps {
  readonly dock: VoiceDockController;
}

/** Formats elapsed seconds as m:ss. */
function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** The assistant bubble for the current run, once it has landed. */
function replyMessage(dock: VoiceDockController): Message | undefined {
  const run = dock.chat.activeRun;
  if (run === null || run.replyId === null) return undefined;
  return dock.chat.feed.find((m) => m.id === run.replyId);
}

// ── round icon button ──────────────────────────────────────────────
function IconBtn({
  icon,
  label,
  onClick,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        width: 30,
        height: 30,
        borderRadius: 15,
        background: T.bgAlt,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <Icon name={icon} size={16} color={T.ink2} />
    </button>
  );
}

// ── popover header ─────────────────────────────────────────────────
function Header({ dock }: VoiceDockProps): JSX.Element {
  const titles: Record<VoiceDockController['phase'], string> = {
    recording: 'Запись',
    processing: 'Обработка',
    answer: 'Ассистент',
    error: 'Не получилось',
  };
  const showChat = dock.phase === 'answer' || dock.phase === 'processing';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 12px 8px 16px',
      }}
    >
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {dock.phase === 'recording' && (
          <span
            class="dock-recdot"
            style={{ width: 9, height: 9, borderRadius: 5, background: T.danger, flexShrink: 0 }}
          />
        )}
        <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
          {titles[dock.phase]}
        </span>
        {dock.phase === 'recording' && (
          <span style={{ fontSize: 13, fontWeight: 600, color: T.ink3, fontVariantNumeric: 'tabular-nums' }}>
            {fmtTime(dock.recSeconds)}
          </span>
        )}
      </div>
      {showChat && (
        <button
          type="button"
          onClick={dock.openInChat}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: T.brand,
            fontSize: 12.5,
            fontWeight: 700,
            fontFamily: T.font,
            padding: '0 4px',
          }}
        >
          В чат
          <Icon name="chevR" size={14} color={T.brand} />
        </button>
      )}
      <IconBtn icon="chevD" label="Свернуть" onClick={dock.collapse} />
      <IconBtn icon="close" label="Закрыть" onClick={dock.close} />
    </div>
  );
}

// ── recording state: native (Web Speech API) ───────────────────────
// Нативный движок не даёт аудио/AnalyserNode — вместо живой волны показываем
// живой промежуточный текст распознавания (aidepo-64).
function NativeRecordingBody({ dock }: VoiceDockProps): JSX.Element {
  const interim = dock.interimText;
  return (
    <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          minHeight: 48,
          background: interim.length > 0 ? T.brandSoft : T.surfaceAlt,
          border: `1px solid ${interim.length > 0 ? T.brandSoft2 : T.border}`,
          borderRadius: 12,
          padding: '10px 12px',
          fontSize: 14,
          color: interim.length > 0 ? T.ink : T.ink3,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {interim.length > 0 ? (
          <span>«{interim}»</span>
        ) : (
          <>
            <span
              class="dock-recdot"
              style={{ width: 9, height: 9, borderRadius: 5, background: T.danger, flexShrink: 0 }}
            />
            <span>Слушаю на устройстве…</span>
          </>
        )}
      </div>
      <div style={{ fontSize: 13, color: T.ink3, fontWeight: 500, textAlign: 'center' }}>
        Говори как живому человеку — распознаю прямо здесь.
      </div>
      <Btn kind="ink" icon="stop" onClick={dock.toggleRecord} style={{ width: '100%' }}>
        Стоп
      </Btn>
    </div>
  );
}

// ── recording state ────────────────────────────────────────────────
function RecordingBody({ dock }: VoiceDockProps): JSX.Element {
  if (dock.engine === 'native') return <NativeRecordingBody dock={dock} />;
  // analyser is null until the mic permission is granted and capture starts.
  if (dock.recorder.analyser === null) {
    return (
      <div
        style={{
          padding: '8px 16px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div class="dock-spin" />
        <span style={{ fontSize: 13.5, color: T.ink2, fontWeight: 600 }}>
          Запрашиваю доступ к микрофону…
        </span>
      </div>
    );
  }
  return (
    <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          background: T.surfaceAlt,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: '8px 10px',
        }}
      >
        <LiveWaveform analyser={dock.recorder.analyser} />
      </div>
      <div style={{ fontSize: 13, color: T.ink3, fontWeight: 500, textAlign: 'center' }}>
        Говори как живому человеку — я слушаю.
      </div>
      <Btn kind="ink" icon="stop" onClick={dock.toggleRecord} style={{ width: '100%' }}>
        Стоп
      </Btn>
    </div>
  );
}

// ── processing state ───────────────────────────────────────────────
function ProcessingBody({ dock }: VoiceDockProps): JSX.Element {
  const run = dock.chat.activeRun;
  const uploading = dock.recorder.state === 'processing' || run === null;
  const stageText = uploading
    ? 'Отправляю запись…'
    : run.stage === 'recognizing'
      ? 'Распознаю речь…'
      : 'Распознал. Думаю над командой…';
  const transcript = run?.transcript ?? '';

  return (
    <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {transcript.length > 0 && (
        <div
          style={{
            background: T.brandSoft,
            border: `1px solid ${T.brandSoft2}`,
            borderRadius: 12,
            padding: '8px 12px',
            fontSize: 14,
            color: T.ink,
            fontWeight: 500,
          }}
        >
          «{transcript}»
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div class="dock-spin" />
        <span style={{ fontSize: 13.5, color: T.ink2, fontWeight: 600 }}>{stageText}</span>
      </div>
    </div>
  );
}

// ── answer state ───────────────────────────────────────────────────
function ReplyBubble({ dock, msg }: { readonly dock: VoiceDockController; readonly msg: Message }): JSX.Element {
  switch (msg.kind) {
    case 'ai_summary':
      return <AISummary msg={msg} onUndo={(id) => void dock.undo(id)} />;
    case 'ai_clarify':
      return (
        <AIClarify
          msg={msg}
          onPick={(opt) => void dock.chat.pickClarifyOption(msg.id, opt)}
        />
      );
    case 'ai_text':
      return msg.time !== undefined ? (
        <AIText text={msg.text} time={msg.time} />
      ) : (
        <AIText text={msg.text} />
      );
    case 'user':
      return <AIText text={msg.text} />;
    default: {
      const _exhaustive: never = msg;
      return _exhaustive;
    }
  }
}

function Composer({ dock }: VoiceDockProps): JSX.Element {
  const [text, setText] = useState<string>('');
  const submit = (): void => {
    if (text.trim().length === 0) return;
    dock.replyText(text);
    setText('');
  };
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        borderTop: `1px solid ${T.divider}`,
      }}
    >
      <IconBtn icon="mic" label="Ответить голосом" onClick={dock.replyVoice} />
      <input
        type="text"
        name="voicedock-reply"
        value={text}
        placeholder="Ответить…"
        onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        style={{
          flex: 1,
          minWidth: 0,
          height: 38,
          padding: '0 12px',
          borderRadius: 12,
          border: `1px solid ${T.border}`,
          background: T.surfaceAlt,
          fontFamily: T.font,
          fontSize: 14,
          color: T.ink,
        }}
      />
      <button
        type="button"
        onClick={submit}
        aria-label="Отправить"
        disabled={text.trim().length === 0}
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          background: text.trim().length === 0 ? T.bgAlt : T.brand,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: text.trim().length === 0 ? 'default' : 'pointer',
          flexShrink: 0,
        }}
      >
        <Icon name="send" size={17} color={text.trim().length === 0 ? T.ink4 : '#fff'} />
      </button>
    </div>
  );
}

function AnswerBody({ dock }: VoiceDockProps): JSX.Element {
  const run = dock.chat.activeRun;
  const transcript = run?.transcript ?? '';
  const reply = replyMessage(dock);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '0 4px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {transcript.length > 0 && (
          <div
            style={{
              fontSize: 12.5,
              color: T.ink3,
              fontWeight: 500,
              padding: '0 12px',
            }}
          >
            Вы: «{transcript}»
          </div>
        )}
        {reply !== undefined ? (
          <ReplyBubble dock={dock} msg={reply} />
        ) : (
          <div style={{ padding: '8px 16px', fontSize: 13.5, color: T.ink3 }}>
            Ответ получен.
          </div>
        )}
        {dock.notice !== null && (
          <div
            style={{
              margin: '4px 16px 0',
              fontSize: 12.5,
              fontWeight: 600,
              color: dock.notice.tone === 'ok' ? T.brandDeep : T.danger,
            }}
          >
            {dock.notice.text}
          </div>
        )}
      </div>
      <Composer dock={dock} />
    </div>
  );
}

// ── error state ────────────────────────────────────────────────────
function ErrorBody({ dock }: VoiceDockProps): JSX.Element {
  return (
    <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Icon name="info" size={20} color={T.danger} />
        <span style={{ fontSize: 13.5, color: T.ink2, fontWeight: 500, lineHeight: 1.4 }}>
          {dock.errorText ?? 'Что-то пошло не так. Попробуй ещё раз.'}
        </span>
      </div>
      <Btn kind="ink" icon="mic" onClick={dock.reRecord} style={{ width: '100%' }}>
        Записать снова
      </Btn>
    </div>
  );
}

function Body({ dock }: VoiceDockProps): JSX.Element {
  switch (dock.phase) {
    case 'recording':
      return <RecordingBody dock={dock} />;
    case 'processing':
      return <ProcessingBody dock={dock} />;
    case 'answer':
      return <AnswerBody dock={dock} />;
    case 'error':
      return <ErrorBody dock={dock} />;
    default: {
      const _exhaustive: never = dock.phase;
      return _exhaustive;
    }
  }
}

// ── collapsed bar ──────────────────────────────────────────────────
function collapsedLabel(dock: VoiceDockController): string {
  switch (dock.phase) {
    case 'recording':
      return `Запись ${fmtTime(dock.recSeconds)}`;
    case 'processing':
      return dock.chat.activeRun?.stage === 'thinking' ? 'Думаю…' : 'Обрабатываю…';
    case 'error':
      return 'Не получилось — нажми, чтобы повторить';
    case 'answer': {
      const reply = replyMessage(dock);
      const text = reply?.text ?? 'Готово';
      return text.length > 38 ? `${text.slice(0, 38)}…` : text;
    }
    default:
      return 'Ассистент';
  }
}

function CollapsedBar({ dock }: VoiceDockProps): JSX.Element {
  const recording = dock.phase === 'recording';
  const processing = dock.phase === 'processing';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 46,
        background: T.surface,
        borderTop: `1px solid ${T.border}`,
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        onClick={dock.expand}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          height: '100%',
          padding: '0 6px 0 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: T.font,
        }}
      >
        {recording ? (
          <span
            class="dock-recdot"
            style={{ width: 9, height: 9, borderRadius: 5, background: T.danger, flexShrink: 0 }}
          />
        ) : processing ? (
          <div class="dock-spin" style={{ width: 14, height: 14 }} />
        ) : (
          <Icon name="mic" size={17} color={T.brand} />
        )}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'left',
            fontSize: 13.5,
            fontWeight: 600,
            color: T.ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {collapsedLabel(dock)}
        </span>
        <Icon name="chevR" size={18} color={T.ink3} />
      </button>
      <button
        type="button"
        onClick={dock.close}
        aria-label="Закрыть"
        style={{
          width: 44,
          height: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="close" size={17} color={T.ink3} />
      </button>
    </div>
  );
}

// ── root ───────────────────────────────────────────────────────────
export function VoiceDock({ dock }: VoiceDockProps): JSX.Element | null {
  if (dock.view === 'hidden') return null;
  if (dock.view === 'collapsed') return <CollapsedBar dock={dock} />;

  return (
    <div style={{ position: 'relative', height: 0 }}>
      <div
        class="dock-popover"
        role="dialog"
        aria-label="Голосовой ассистент"
        style={{
          position: 'absolute',
          left: 8,
          right: 8,
          bottom: 12,
          background: T.surface,
          borderRadius: 18,
          border: `1px solid ${T.border}`,
          boxShadow: T.shadow3,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '68vh',
          overflow: 'hidden',
          zIndex: 40,
        }}
      >
        <Header dock={dock} />
        <div style={{ overflowY: 'auto' }}>
          <Body dock={dock} />
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 5,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: `8px solid ${T.surface}`,
          zIndex: 41,
        }}
      />
    </div>
  );
}
